import { z } from 'zod';

import { csvParam, daySchema, sortDirSchema, type Page } from './common.js';
import { type AdPlatform } from './enums.js';

/**
 * LEAD — okuma sözleşmesi (Faz 7).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `analytics.ts`in içinde değil
 * ═══════════════════════════════════════════════════════════════════════════
 * `keywords.ts` ile aynı gerekçe, tersinden: lead bir METRİK değil bir
 * KAYIT. `metrics_daily` gün × varlık greninde toplanabilir sayılar tutuyor;
 * lead tek tek, isimli, kişisel veri taşıyan satırlar. Aynı dosyaya koymak
 * "lead'lerin ortalamasını al" gibi anlamsız bir şeyi tip düzeyinde mümkün
 * kılardı.
 *
 * Huni metrikleri (`addToCart`, `purchases`…) ise `analytics.ts`te ve orada
 * kalmalı: onlar gerçekten toplanabilir sayılar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `fields` KİŞİSEL VERİ — bu dosyanın en önemli satırı
 * ═══════════════════════════════════════════════════════════════════════════
 * Formun cevapları ad, telefon, e-posta ve reklamverenin sorduğu her şeyi
 * taşıyor. Sonucu üç somut kural:
 *
 *   1. `fields` LOGA, hata mesajına ve denetim kaydına GİRMEZ. Bir lead
 *      satırının kaydedilememesi loglanır, İÇERİĞİ loglanmaz.
 *   2. Liste yanıtı `fields`i TAM DÖNDÜRMEZ (`LeadListRow.preview`) —
 *      tablo ekranı iki-üç alan gösteriyor, tamamını görmek ayrı bir istek
 *      ve ayrı bir denetim satırı.
 *   3. CSV dışa aktarma tamamını döndürür ama bu KASITLI bir eylem:
 *      kullanıcı indirme düğmesine basıyor ve bu bir dışa aktarma olarak
 *      kaydediliyor.
 *
 * GDPR silme akışı (`workspaces` alanı) `leads` tablosunu workspace
 * cascade'iyle zaten süpürüyor; tek tek lead silme ucu BİLEREK YOK —
 * platformda kayıt duruyor ve bizden silmek onu yok etmiyor, yalnız
 * kullanıcıya yok etmiş gibi gösterirdi.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Liste
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sıralanabilir kolonlar — DAR bir liste.
 *
 * `submittedAt` dışında sıralanacak pek bir şey yok ve olması da istenmez:
 * `fields` içindeki bir cevaba göre sıralamak, JSON içinde tam tarama demek
 * ve o kolonda indeks yok. Liste burada daraltılıyor ki `ORDER BY`a
 * kullanıcıdan gelen serbest metin asla ulaşmasın.
 */
export const LEAD_SORT_FIELDS = ['submittedAt', 'createdAt'] as const;
export type LeadSort = (typeof LEAD_SORT_FIELDS)[number];

export const leadListQuerySchema = z.object({
  /// Serbest arama — `fields` değerlerinde ve kampanya adında.
  /// ⚠️ Kişisel veri içinde arama: ucun kendisi denetime yazılıyor, arama
  /// METNİ yazılmıyor (aranan telefon numarası denetim kaydına düşmesin).
  q: z.string().trim().max(200).optional(),
  from: daySchema.optional(),
  to: daySchema.optional(),
  platforms: csvParam.pipe(z.array(z.enum(['META', 'GOOGLE', 'TIKTOK'])).max(4)).optional(),
  adAccountIds: csvParam.pipe(z.array(z.string().max(64)).max(50)).optional(),
  campaignIds: csvParam.pipe(z.array(z.string().max(64)).max(50)).optional(),
  sort: z.enum(LEAD_SORT_FIELDS).default('submittedAt'),
  dir: sortDirSchema,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type LeadListQuery = z.infer<typeof leadListQuerySchema>;

/**
 * Liste satırı — `fields`in TAMAMI değil, önizlemesi.
 *
 * `preview` en fazla `LEAD_PREVIEW_FIELDS` kadar alan taşıyor ve hangileri
 * olduğunu sunucu seçiyor (`pickPreviewFields`). Arayüzün "ilk üçünü al"
 * demesi, form alan sırasına göre bir gün telefon bir gün adres göstermek
 * olurdu.
 */
export interface LeadListRow {
  id: string;
  platform: AdPlatform;
  submittedAt: string;
  createdAt: string;
  campaignId: string | null;
  campaignName: string | null;
  adId: string | null;
  adName: string | null;
  adAccountId: string;
  adAccountName: string | null;
  preview: Record<string, string>;
  /// `fields`te toplam kaç alan var — önizleme kırpılmışsa arayüz
  /// "+4 alan daha" diyebilsin.
  fieldCount: number;
}

/// Önizlemede gösterilecek en fazla alan sayısı. Üç, bir tablo satırına
/// taşmadan sığan ve lead'i tanımaya yeten sayı (tipik olarak ad, e-posta,
/// telefon).
export const LEAD_PREVIEW_FIELDS = 3;

/**
 * Önizlemede ÖNCELİKLİ alan adları — küçük harfe indirgenmiş parçalar.
 *
 * Meta form alanlarının `name`i standart formlarda İngilizce sabit
 * (`full_name`, `email`, `phone_number`), özel sorularda reklamverenin
 * yazdığı serbest metin. Bu liste standart olanları öne çekiyor; eşleşme
 * yoksa alan sırası korunuyor.
 *
 * ⚠️ Bu bir GÖSTERİM tercihi, bir veri kararı değil. Eşleşmeyen bir form
 * hiçbir şey kaybetmiyor — yalnız ilk üç alanı sırayla gösteriyor.
 */
export const LEAD_PREVIEW_PRIORITY = [
  'full_name',
  'name',
  'ad',
  'email',
  'e-posta',
  'eposta',
  'phone',
  'telefon',
  'company',
  'sirket',
] as const;

export type LeadPage = Page<LeadListRow>;

// ═══════════════════════════════════════════════════════════════════════════
// Detay
// ═══════════════════════════════════════════════════════════════════════════

/// Tek lead — `fields`in TAMAMI. Ayrı bir uç olmasının sebebi dosya
/// başlığındaki 2. kural.
export interface LeadDetail extends Omit<LeadListRow, 'preview'> {
  fields: Record<string, string>;
  /// Platformdaki kimlik. Destek konuşmalarında "şu lead" diye
  /// gösterilebilmesi için dönüyor; başka bir işlevi yok.
  platformRef: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Özet
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Lead sayacı — panel kutusu ve "lead akışı durdu" teşhisi için.
 *
 * 🔴 `metrics_daily`den GELMİYOR ve gelmemeli: lead satırı bir olay,
 * `metrics_daily` bir toplam. Aynı günün lead sayısı iki kaynaktan
 * okunsaydı ikisi kaçınılmaz olarak ayrışırdı (platform dönüşümü geriye
 * dönük revize ediyor, lead satırı revize edilmiyor) ve hangisinin doğru
 * olduğu sorulamazdı.
 */
export interface LeadSummary {
  total: number;
  /// Gün gün lead sayısı — sıfır günler DE dolu (`0` ile). Eksik gün
  /// bırakmak, grafikte çizginin atlaması demek olurdu ve "lead gelmedi" ile
  /// "o gün yok" karışırdı.
  daily: { date: string; count: number }[];
  /// En çok lead getiren kampanyalar.
  topCampaigns: { campaignId: string; campaignName: string; count: number }[];
  range: { from: string; to: string };
}

export const leadSummaryQuerySchema = z.object({
  from: daySchema.optional(),
  to: daySchema.optional(),
  platforms: csvParam.pipe(z.array(z.enum(['META', 'GOOGLE', 'TIKTOK'])).max(4)).optional(),
  adAccountIds: csvParam.pipe(z.array(z.string().max(64)).max(50)).optional(),
  campaignIds: csvParam.pipe(z.array(z.string().max(64)).max(50)).optional(),
});
export type LeadSummaryQuery = z.infer<typeof leadSummaryQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yardımcılar — sunucu ve arayüz AYNI seçimi yapsın
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `fields`ten önizleme alanlarını seçer.
 *
 * Saf ve deterministik: aynı lead her istekte AYNI üç alanı gösteriyor.
 * Rastgele ya da sıraya bağlı bir seçim, kullanıcının tabloda bir gün
 * telefonu bir gün adresi görmesi demek olurdu.
 */
export function pickPreviewFields(
  fields: Record<string, string>,
  limit: number = LEAD_PREVIEW_FIELDS,
): Record<string, string> {
  const entries = Object.entries(fields);
  if (entries.length <= limit) return Object.fromEntries(entries);

  const scored = entries.map(([key, value], index) => {
    const lower = key.toLowerCase();
    const hit = LEAD_PREVIEW_PRIORITY.findIndex((needle) => lower.includes(needle));
    // Eşleşmeyen alan listenin SONUNA gidiyor ama kendi sırasını koruyor:
    // `index` ikincil anahtar, yani eşleşmesiz bir formda sonuç form
    // sırasının ilk üçü oluyor.
    return { key, value, rank: hit === -1 ? LEAD_PREVIEW_PRIORITY.length : hit, index };
  });

  scored.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index));
  return Object.fromEntries(scored.slice(0, limit).map((row) => [row.key, row.value]));
}

/**
 * Ham `Lead.fields` JSON'unu güvenli bir `Record<string, string>`e çevirir.
 *
 * Kolon `Json` ve içine yazan tek yer senkron — ama okuyan taraf yine de
 * DOĞRULAMAK zorunda: elle yazılmış bir satır, eski bir biçim ya da bozuk
 * bir migration, arayüzde `[object Object]` basan bir tabloya dönüşürdü.
 *
 * ⚠️ FIRLATMIYOR. Tek bozuk satır yüzünden lead listesinin tamamının 500
 * vermesi, koruduğu şeyden pahalı olurdu; bozuk satır boş alanlarla
 * görünüyor ve `fieldCount: 0` bunu ele veriyor.
 */
export function parseLeadFields(raw: unknown): Record<string, string> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
    else if (typeof value === 'number' || typeof value === 'boolean') out[key] = String(value);
    // Nesne/dizi ATLANIYOR: düzleştirme kararı senkronun (adapter) işi ve
    // okuma tarafında ikinci bir düzleştirme, iki farklı gösterim üretirdi.
  }
  return out;
}
